import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SearchInput } from "component/TableSearchInput";
import {
  ArrowUpDown,
  CheckCircleIcon,
  Notification as NotificationIcon,
  OrangeLock,
  Settings,
} from "asset/icons";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "component/ui/Dropdown";
import useGetRequest from "hooks/useGetRequest";
import sortListByDate from "utilities/sortListByDate";
import { useDispatch, useSelector } from "react-redux";
import NotificationDropdown from "./NotificationDropdown";
import capitalizeWords from "utilities/capitalizeWords";
import Avatar from "component/avatar";
import { cn } from "lib/utils";
import { Add, HamburgerMenu, Lock, LogoutCurve, Setting2 } from "iconsax-reactjs";
import { Drawer, DrawerContent, DrawerTrigger } from "../ui/Drawer";
import NewSideNav from "./NewSideNav";
import axios from "axios";
import { LOGOUT_USER } from "../../store/action";
import NotificationBar from "./NotificationBar";
import AdditionalInfoBanner from "./AdditionalInfoBanner";
import { useNavigate } from "react-router-dom";
import useUserDetails from "hooks/useUserDetails";
import { useQuery } from "react-query";
import { fetchBusinessData } from "api/fetchers";
import { Spinner } from "react-bootstrap";

const getBusinessDefaultCurrency = (business) => {
  const countryCode = String(
    business?.businessCountry?.code || business?.countryCode || ""
  ).toUpperCase();
  const countryName = String(
    business?.businessCountry?.name || business?.country || ""
  ).toLowerCase();

  return countryCode === "NG" || countryName === "nigeria" ? "NGN" : "USD";
};

const getWalletCurrencies = (response) => {
  const wallets =
    (Array.isArray(response?.data?.data) && response.data.data) ||
    (Array.isArray(response?.data) && response.data) ||
    [];

  return {
    currencies: wallets
      .map((wallet) => String(wallet?.currency || wallet?.code || "").toUpperCase())
      .filter(Boolean),
    wallets,
  };
};

const EMPTY_ARRAY = [];

const NewDashboardLayout = ({ children, direction = "left" }) => {
  const { users: user, business, currency, businessList } = useSelector((state) => state);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [switchInProgress, setSwitchInProgress] = useState(false); // Track switch progress
  const [selectedBizId, setSelectedBizId] = useState();
  const [fetchNotifications, _notifications, notificationsLoading, , , setNotificationsLoading] =
    useGetRequest();
  const { currentBusinessAccount, isLoadingCurrentBusinessAccount } = useUserDetails()

  const { data: fetchedBusinesses, isFetching: isFetchingBusinesses } = useQuery({
    queryKey: ['businessList'],
    queryFn: fetchBusinessData,
    onSuccess: (data) => {
      const payload = data?.data || data;
      if (payload) {
        dispatch({ type: "BUSINESSES_LIST_RES", payload });
      }
    },
    enabled: !businessList || businessList.length <= 1,
  });

  const allBusinesses = fetchedBusinesses || businessList || EMPTY_ARRAY;

  const businessesData = useMemo(() => {
    if (!allBusinesses.length) {
      return { businesses: EMPTY_ARRAY, selected: undefined };
    }
    const activeBusinesses = allBusinesses.filter(({ isDeleted }) => !isDeleted);

    return {
      businesses: activeBusinesses.filter(({ isCurrent }) => !isCurrent),
      selected: activeBusinesses.find(({ isCurrent }) => isCurrent),
    };
  }, [allBusinesses]);

  useEffect(() => {
    if (!selectedBizId) return;

    let cancelled = false;
    const switchBusiness = async () => {
      const nextBusiness = (allBusinesses || []).find(({ _id }) => _id === selectedBizId);
      if (!nextBusiness) {
        setSelectedBizId(null);
        return;
      }

      setSwitchInProgress(true);
      dispatch({ type: "BUSINESS_SWITCH_START" });

      try {
        let currencies = [];
        let nextWallets = [];

        if (String(nextBusiness?.status || "").toUpperCase() === "APPROVED") {
          try {
            const walletResponse = await axios.get("/v1/wallets/accounts", {
              headers: { "x-business-id": selectedBizId },
            });
            const walletData = getWalletCurrencies(walletResponse);
            currencies = walletData.currencies;
            nextWallets = walletData.wallets;
          } catch (walletError) {
            console.warn("Unable to prefetch wallets for the selected business:", walletError);
          }
        }

        if (cancelled) return;

        const savedCurrency = localStorage.getItem(`default:${selectedBizId}`);
        const preferredCurrency = String(
          savedCurrency || nextBusiness?.currency || getBusinessDefaultCurrency(nextBusiness)
        ).toUpperCase();
        const nextCurrency = currencies.includes(preferredCurrency)
          ? preferredCurrency
          : currencies.includes(getBusinessDefaultCurrency(nextBusiness))
            ? getBusinessDefaultCurrency(nextBusiness)
            : currencies[0] || getBusinessDefaultCurrency(nextBusiness);

        await axios.put(`/v1/business/current/${selectedBizId}`);
        if (cancelled) return;

        if (business?._id && currency) {
          localStorage.setItem(`default:${business._id}`, String(currency).toUpperCase());
        }
        localStorage.setItem(`default:${selectedBizId}`, nextCurrency);
        localStorage.setItem("default", nextCurrency);

        dispatch({
          type: "BUSINESS_SWITCH_COMPLETE",
          payload: {
            business: nextBusiness,
            currency: nextCurrency,
            wallets: nextWallets,
          },
        });

        navigate("/dashboard");
        window.location.reload();
      } catch (error) {
        if (!cancelled) {
          console.error("Unable to switch business:", error);
          dispatch({ type: "BUSINESS_SWITCH_CANCEL" });
          setSwitchInProgress(false);
          setSelectedBizId(null);
        }
      }
    };

    switchBusiness();
    return () => {
      cancelled = true;
    };
  }, [business?._id, allBusinesses, currency, dispatch, navigate, selectedBizId]);

  // useEffect(() => {
  //   // Trigger notification bar display only if business details are fully fetched
  //   if (business) {
  //     fetchBusinesses("/v1/business/user", null, false);
  //     setBusinessLoaded(true);

  //     const timer = setTimeout(() => {
  //       setShowNotificationBar(true);
  //     }, 2000);

  //     return () => clearTimeout(timer);
  //   }
  // }, [business]);

  useLayoutEffect(() => {
    const storedNavState = sessionStorage.getItem("isNavOpen");
    if (storedNavState !== null) {
      setIsOpen(storedNavState === "true");
    }
  }, []);

  const notifications = sortListByDate(_notifications ?? [])
    .reverse()
    .map(({ record }) => record)
    .flat();

  const accountName = capitalizeWords(
    `${business?.name ?? `${user?.firstName ?? ""} ${user?.lastName ?? ""}`}`
  );
  const accountNameArr = accountName?.split(" ");
  const accountImg =
    accountName === capitalizeWords(business?.name ?? "")
      ? business?.businessImage?.url
      : user?.profileImage?.url;

  useEffect(() => {
    if (business?._id) {
      fetchNotifications("/v1/notification/all");
    } else {
      setNotificationsLoading(false);
    }
  }, [business?._id]);

  const toggleNav = useCallback(() => {
    setIsOpen((prev) => {
      sessionStorage.setItem("isNavOpen", String(!prev));
      return !prev;
    });
  }, []);

  const tabletView = !isOpen
    ? `lg:w-[calc(100%-108px)]  xl:w-[calc(100%-108px)]`
    : `lg:w-[calc(100%-220px)]  xl:w-[calc(100%-248px)]`;

  const tabletViewWidth = !isOpen ? `lg:w-[108px] xl:w-[108px]` : `lg:w-[220px] xl:w-[248px]`;

  // Conditional logic for rendering Notification bar
  const renderNotificationBar = () => {
    const stage = business?.stage ?? null;

    let actionPath = "/onboarding/business-details";

    if (stage === 2) {
      actionPath = "/onboarding/contact-details";
    } else if (stage === 3) {
      if (business?.type === "LLC") {
        actionPath = "/onboarding/business-verification/compliance/llc";
      } else if (business?.type === "SoleProprietorship") {
        actionPath = "/onboarding/business-verification/compliance/soleproprietorship";
      }
    } else if (stage === 4) {
      actionPath = "/onboarding/business-verification/wallets";
    } else if (stage === 5) {
      actionPath = "/onboarding/business-ownership/control-person";
    } else if (stage === 6) {
      actionPath = "/onboarding/business-ownership/directors";
    } else if (stage === 7) {
      actionPath = "/onboarding/summary";
    }

    if (
      !business ||
      (Array.isArray(business) && business.length === 0) ||
      business?.status === "PENDING" || business?.status === "STARTED"
    ) {
      return (
        <NotificationBar
          message="Your account isn’t fully activated."
          actionText="Complete registration"
          icon={<OrangeLock color="white" className="w-5 h-5" />}
          className="flex justify-center bg-[#FFEFC6] text-[#475467]"
          actionPath={actionPath}
        />
      );
    }

    if (business?.status === "SUBMITTED" || business?.status === "PROCESSING" || business?.status === "UNDER REVIEW") {
      return (
        <NotificationBar
          message="Your account is under review. This usually takes between 24-48 hours."
          className="flex justify-center bg-[#EBE4FF] text-[#6F00FF]"
        />
      );
    }

    if (business?.status === "APPROVED" && !user?.isMfaEnabled) {
      return (
        <NotificationBar
          message="Your account isn’t fully secure yet."
          actionText="Set up 2FA"
          trailingText="to protect your account and transactions."
          icon={<Lock variant="Bold" color="#DB6704" className="w-5 h-5" />}
          className="flex justify-center bg-[#FFEFC6] text-[#475467]"
          actionClassName="text-[#DB6704]"
          actionPath="/auth/setup-2fa"
        />
      );
    }

    return null;
  };

  const noPendingDataToSubmit = currentBusinessAccount?.informationRequestStatus === 'ACTION_REQUIRED';

  return (
    <Drawer direction={direction}>
      <>
        {(!isLoadingCurrentBusinessAccount && noPendingDataToSubmit) && <AdditionalInfoBanner />}
        <div className="flex">
          <aside
            className={cn(
              `transition-all hidden lg:flex flex-col bg-bg-color-10 p-3 `,
              tabletViewWidth
            )}
          >
            <NewSideNav isCollapsed={isOpen} toggleNav={toggleNav} />
          </aside>
          <div
            className={cn(
              `transition-all w-full ease-linear relative h-screen overflow-x-hidden overflow-y-auto`,
              tabletView
            )}
          >
            <div className="h-[80px] sticky top-0 w-full bg-bg-color-10 z-[100] m-0 flex items-center py-3 pr-3 pl-2 md:border justify-between">
              <div className="hidden items-center xl:flex max-w-[400px] w-full">
                <SearchInput disabled="true" />
              </div>
              <div className="flex items-center xl:hidden">
                <>
                  <DrawerTrigger asChild className="flex lg:hidden">
                    <HamburgerMenu size={24} />
                  </DrawerTrigger>

                  <DrawerContent className="z-[999]">
                    <aside className="w-[248px] flex xl:hidden h-full rounded-r-md flex-col bg-light-grey p-3">
                      <NewSideNav isMobile />
                    </aside>
                  </DrawerContent>
                </>
              </div>

              <div className="flex items-center justify-end w-1/2 gap-x-3 md:gap-x-10">
                <DropdownMenu>
                  <DropdownMenuTrigger className="bg-transparent">
                    <NotificationIcon />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="p-0 bg-white border-0 z-[200] shadow-none520:rounded-lg w-screen 520:w-[450px] overflow-hidden h-[496px]">
                    <NotificationDropdown
                      notifications={notifications}
                      notificationsLoading={notificationsLoading}
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
                <Link to="/account/home">
                  <Settings />
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center justify-between p-2 bg-transparent cursor-pointer gap-x-2">
                    <Avatar
                      img={accountImg}
                      className={`${accountNameArr?.length > 3 && "text-[10px] "
                        } size-[42px]`}
                      fullName={accountName}
                    />
                    <div className="flex-col items-start justify-center hidden md:flex">
                      <p className="text-sm text-normal overflow-clip whitespace-nowrap text-ellipsis max-w-[130px]">
                        {accountName}
                      </p>
                      <span className="text-xs font-light text-[#6F739B]">
                        {capitalizeWords(business?.status)}
                      </span>
                    </div>
                    <div>
                      <ArrowUpDown />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="p-[1px] bg-[#F7F8FD] z-[200] w-[290px] shadow-none 520:rounded-lg rounded-lg overflow-hidden border border-[#E7E9FB]">
                    <div className="mb-[2px] bg-white rounded-t-lg  py-2">
                      <p className="text-[#6F739B] text-sm font-light px-3 ">
                        Switch business
                      </p>
                    </div>
                    <div className="max-h-[227px] h-full bg-white scroll-smooth !border-y border-[#EBEDF9] overflow-y-auto px-3 py-2 flex flex-col gap-y-3">
                      {businessesData?.selected && (
                        <AccountInfo
                          setSelectedBizId={setSelectedBizId}
                          biz={businessesData?.selected}
                          disabled={switchInProgress}
                        />
                      )}
                      {isFetchingBusinesses ? <p className="text-[#6F739B] text-sm font-light px-3 ">
                        <div className="flex items-center justify-center gap-x-2">
                          <Spinner />
                          <span>Loading...</span>
                        </div>
                      </p> : businessesData?.businesses?.length ? businessesData?.businesses?.map((biz, index) => {
                        return (
                          <AccountInfo
                            setSelectedBizId={setSelectedBizId}
                            biz={biz}
                            disabled={switchInProgress}
                            key={index}
                          />
                        );
                      }) : null}
                    </div>
                    <div className="m-[2px] bg-white rounded-sm">
                      <Link
                        to={"/onboarding/business-details"}
                        className="flex items-center px-3 py-3 text-sm font-normal text-gray-90 gap-x-2"
                      >
                        <Add size={20} className="text-gray-90" /> Add business
                      </Link>
                    </div>
                    <div className="m-[2px] bg-white rounded-sm">
                      <Link
                        className="flex items-center px-3 py-3 text-sm font-normal text-gray-90 gap-x-2"
                        to="/account/home"
                      >
                        <Setting2 size={20} color="#6F739B" /> Manage account
                      </Link>
                      <button
                        className="flex items-center px-3 py-3 text-sm font-normal text-gray-90 gap-x-2"
                        onClick={() => LOGOUT_USER("", dispatch)}
                      >
                        <LogoutCurve size={20} color="#6F739B" /> Logout
                      </button>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            {isFetchingBusinesses ? null : ['NONE', 'SUBMITTED'].includes(currentBusinessAccount?.informationRequestStatus) ? renderNotificationBar() : null}
            <div className="bg-bg-color-10 w-11/12 md:w-[97%] mx-auto pb-2">{children}</div>
          </div>
        </div>
      </>
    </Drawer>
  );
};

export default NewDashboardLayout;

const AccountInfo = ({ setSelectedBizId, biz, disabled = false }) => {
  return (
    <div
      onClick={() => {
        if (disabled || biz?.isCurrent) return;
        setSelectedBizId && setSelectedBizId(biz._id);
      }}
      className={cn(
        "flex items-center gap-x-2",
        disabled || biz?.isCurrent ? "cursor-default" : "cursor-pointer"
      )}
      aria-disabled={disabled}
    >
      <div>
        <Avatar
          img={biz?.businessImage?.url}
          className={`${biz?.name?.toLowerCase()?.length > 3 && "text-[10px] "
            } size-[32px] block`}
          fullName={biz?.name?.toLowerCase()}
        />
      </div>
      <p className="text-sm font-normal text-gray-90">{capitalizeWords(biz?.name)}</p>
      {biz?.isCurrent ? (
        <div className="flex items-center justify-center ml-auto !bg-white p-0 rounded-full shrink-0">
          <CheckCircleIcon />
        </div>
      ) : (
        <div className="flex ml-auto border rounded-full shrink-0 size-4"></div>
      )}
    </div>
  );
};
